import { useState, useEffect, useCallback, useRef } from 'react';
import ProgressService from '../services/ProgressService';
import { SSE_ENABLED, POLLING_ENABLED, POLLING_INTERVAL } from '../config/app';

export const useSSEProgress = (jobId, options = {}) => {
  const [progress, setProgress] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [error, setError] = useState(null);
  const progressServiceRef = useRef(null);
  
  // Configuration options - respect global config
  const {
    enableSSE = SSE_ENABLED, // Use global config instead of defaulting to true
    fallbackToPolling = POLLING_ENABLED, // Use global config
    pollingInterval = POLLING_INTERVAL, // Use global config
    maxReconnectAttempts = 5
  } = options;
  
  // Initialize progress service on first render
  useEffect(() => {
    if (!progressServiceRef.current) {
      progressServiceRef.current = new ProgressService();
    }
    
    return () => {
      if (progressServiceRef.current) {
        progressServiceRef.current.cleanup();
      }
    };
  }, []);
  
  // Progress update callback
  const handleProgressUpdate = useCallback((progressData) => {
    setProgress(progressData);
    setError(null);
    
    // Update connection status based on data source
    if (connectionStatus === 'connecting' || connectionStatus === 'reconnecting') {
      setConnectionStatus(enableSSE ? 'connected' : 'polling');
    }
    
    // Emit custom event for other components to listen
    window.dispatchEvent(new CustomEvent('progressUpdate', {
      detail: { jobId, progress: progressData }
    }));
  }, [jobId, connectionStatus, enableSSE]);
  
  // Error callback
  const handleError = useCallback((error) => {
    console.error('Progress update error:', error);
    setError(error.message || 'Connection error');
    
    // Update connection status based on error type
    if (error.message && error.message.includes('reconnect')) {
      setConnectionStatus('reconnecting');
    } else if (error.message && error.message.includes('polling')) {
      setConnectionStatus('polling');
    } else {
      setConnectionStatus('error');
    }
  }, []);
  
  // Completion callback
  const handleComplete = useCallback((completionData) => {
    setProgress(prev => ({ ...prev, status: 'completed', ...completionData }));
    setConnectionStatus('completed');
    
    // Emit completion event
    window.dispatchEvent(new CustomEvent('jobCompleted', {
      detail: { jobId, completion: completionData }
    }));
  }, [jobId]);
  
  // Subscribe to progress updates
  useEffect(() => {
    if (!jobId || !progressServiceRef.current) return;
    
    setConnectionStatus('connecting');
    setError(null);
    
    if (enableSSE) {
      // Use SSE with fallback
      progressServiceRef.current.subscribeToProgress(
        jobId,
        handleProgressUpdate,
        handleError,
        handleComplete,
        {
          enableSSE: true,
          fallbackToPolling,
          pollingInterval,
          maxReconnectAttempts
        }
      );
    } else if (fallbackToPolling) {
      // Direct polling fallback
      progressServiceRef.current.startPolling(
        jobId,
        handleProgressUpdate,
        handleError,
        handleComplete,
        pollingInterval
      );
      setConnectionStatus('polling');
    }
    
    return () => {
      if (progressServiceRef.current) {
        progressServiceRef.current.unsubscribeFromProgress(jobId);
      }
    };
  }, [jobId, enableSSE, fallbackToPolling, pollingInterval, maxReconnectAttempts, handleProgressUpdate, handleError, handleComplete]);
  
  // Manual refresh function
  const refreshProgress = useCallback(async () => {
    if (!jobId) return;
    
    try {
      const apiBaseUrl = import.meta.env.VITE_API_URL || 'https://web-api-rqa6jhyvwa-uc.a.run.app';
      const response = await fetch(`${apiBaseUrl}/api/v1/progress/${jobId}`);
      if (response.ok) {
        const progressData = await response.json();
        setProgress(progressData);
        setError(null);
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (err) {
      setError(err.message);
    }
  }, [jobId]);
  
  return {
    progress,
    connectionStatus,
    error,
    refreshProgress,
    isConnected: connectionStatus === 'connected',
    isPolling: connectionStatus === 'polling',
    isReconnecting: connectionStatus === 'reconnecting',
    hasError: !!error
  };
};

export default useSSEProgress;
